import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Context } from '../controls/advanced-gridview/models/context';
import { FilterInfos } from '../controls/advanced-gridview/models/filter-infos';
import { Results } from '../controls/advanced-gridview/models/results-T';
import { SortInfos } from '../controls/advanced-gridview/models/sort-infos';
import { BatchModification } from '../controls/inline-editable-table/models/batch-modifications';
import { BatchResult } from '../controls/inline-editable-table/models/batch-result';
import { BatchEditableTableServiceInterface } from '../controls/inline-editable-table/services/batch-editable-table-service-interface';
import { ChannelProcess } from '../models/channelProcess';
import { CrudService } from './crud-service';
import { MemoryService } from './memory.service';

@Injectable({
    providedIn: 'root',
})
export class ChannelProcessService extends CrudService implements BatchEditableTableServiceInterface<ChannelProcess> {
    constructor(
        private http: HttpClient,
        public override memoryService: MemoryService
    ) {
        super(memoryService);
    }

    List(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos, pageNumber = 0, pageSize = 100): Observable<Results<ChannelProcess>> {
        let params = new Map<string, string | number | boolean>();
        params.set('from', pageNumber);
        params.set('size', pageSize);

        const httpOptions = this.getListHttpOptions(context, filters, generalFilters, sortInfos, params);

        // Appel de la Web Api
        return this.http.get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${context.value}/Processes`, httpOptions).pipe(
            map((x) => {
                var res = Results.getCopyFrom<ChannelProcess>(ChannelProcess, x);
                return res;
            })
        );
    }

    Export(context: Context, filters: FilterInfos[], generalFilters: FilterInfos[], sortInfos: SortInfos) {
        const headers = this.getExportHeader(context, filters, generalFilters, sortInfos);

        // Appel de la Web Api
        return this.http
            .get(this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${context.value}/Processes/export`, {
                headers: headers,
                responseType: 'blob',
            })
            .pipe(
                map((res: any) => {
                    const blob = new Blob([res], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                    return blob;
                })
            );
    }

    update(channelId: number, channelProcessId: number, channelProcess: ChannelProcess): Observable<ChannelProcess> {
        // Appel de la Web Api
        return this.http
            .put<ChannelProcess>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${channelId}/Processes/${channelProcessId}`,
                channelProcess,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    delete(channelId: number, channelProcessId: number): Observable<boolean> {
        // Appel de la Web Api
        return this.http
            .delete<ChannelProcess>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${channelId}/Processes/${channelProcessId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return true;
                })
            );
    }

    add(channelId: number, channelProcess: ChannelProcess): Observable<ChannelProcess> {
        // Appel de la Web Api
        return this.http
            .post<ChannelProcess>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${channelId}/Processes`,
                channelProcess,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    get(channelId: number, channelProcessId: number): Observable<ChannelProcess> {
        // Appel de la Web Api
        return this.http
            .get<ChannelProcess>(
                this.baseUrl + `/api/Organizations/${this.organizationId}/views/${this.viewId}/Channels/${channelId}/Processes/${channelProcessId}`,
                this.getDefaultHttpOptions()
            )
            .pipe(
                map((x) => {
                    return x;
                })
            );
    }

    public applyBatchModifications(context: Context, modifications: BatchModification<ChannelProcess>): Observable<BatchResult[]> {
        let requests = new Array<Observable<BatchResult>>();

        modifications.deletes.forEach((x) => {
            requests.push(this.delete(x.data.receivingChannelId, x.data.id as number).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        modifications.updates.forEach((x) => {
            requests.push(this.update(x.data.receivingChannelId, x.data.id, x.data).pipe(map((y) => new BatchResult(x.data.editableId, true))));
        });

        modifications.creates.forEach((x) => {
            x.data.receivingChannelId = context.value as number;
            requests.push(this.add(x.data.receivingChannelId, x.data).pipe(map((y) => new BatchResult(x.data.id, true))));
        });

        return forkJoin(requests);
    }
}
